<template>
  <base-page-layout ref="base-page" class="comparativo-produtos" title="Comparativo de categorias" :filters="['lojas', 'segmentos', 'categorias']">
    <mf-loading loader="categories-tree" loader-type="dialog">
      <decomposition-filters v-if="lastUpdate && lastUpdate.last_updated" v-model="filters" :last-updated="formatedLastUpdated" />

      <mf-card-container
        class="mt-4"
        title="Comparativo de categorias - Lojas"
        description="Aqui você vê as categorias selecionadas no filtro, loja a loja. Cada célula desta tabela mostra uma comparação dos indicadores da categoria no período referência versus o período atual, assim a categoria é comparada com ela mesma, para uma mesma loja. Todas as colunas da tabela são ordenáveis."
      >
        <template v-slot:headers>
          <export-data
            :disabled="loadingComparisonData"
            :data-fetcher="getComparisonDataToExport"
            :filename="`Comparativo de Categorias - Lojas - ${resultsTabs[resultTab].name} - ${resultsTabs[resultTab].childrenTabs[resultSubtab].name}`"
            ghost
          />
        </template>
        <mf-loading :boolean-loader="loadingComparisonData" loader-type="table">
          <mf-tabs-menu v-model="resultTab" highlight-color="white" class="mt-4" :tabs="resultsTabs" :disabled="loadingComparisonData">
            <template v-slot:children>
              <mf-tabs-menu v-model="resultSubtab" class="mt-4" :tabs="resultsTabs[resultTab].childrenTabs" :disabled="loadingComparisonData" />
            </template>
          </mf-tabs-menu>
          <results-table
            ref="comparisonResultsTable"
            :categories="categories"
            :units="filteredUnits"
            :comparison-data="comparisonData"
            :metric-key="resultsTabs[resultTab].childrenTabs[resultSubtab].metricKey"
            :custom-reference-getter="resultsTabs[resultTab].childrenTabs[resultSubtab].customReferenceGetter"
            :custom-current-getter="resultsTabs[resultTab].childrenTabs[resultSubtab].customCurrentGetter"
            :custom-percentage-calculator="resultsTabs[resultTab].childrenTabs[resultSubtab].customPercentageCalculator"
            :custom-color-value-calculator="resultsTabs[resultTab].childrenTabs[resultSubtab].customColorValueCalculator"
            :loading="loadingComparisonData"
            :formatter="resultsTabs[resultTab].childrenTabs[resultSubtab].formatter"
            :metric-description="`${resultsTabs[totalResultTab].name} - ${resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].name}`"
            :cell-information-configuration="{
              metric: `${resultsTabs[resultTab].name} - ${resultsTabs[resultTab].childrenTabs[resultSubtab].name}`,
              metricKey: resultsTabs[resultTab].childrenTabs[resultSubtab].metricKey,
              ...(resultsTabs[resultTab].childrenTabs[resultSubtab].cellInformationConfiguration || {}),
            }"
            :reference-date="filters.referencyDate"
            :current-date="filters.currentDate"
            :quick-action="resultsTabs[resultTab].childrenTabs[resultSubtab].quickAction"
            :is-empty="isComparisonEmpty"
            group-by-units
          />
        </mf-loading>
      </mf-card-container>

      <mf-card-container
        class="mt-4"
        title="Comparativo de categorias - Totais"
        description="Aqui você vê as categorias selecionadas no filtro, totalizada no período. Cada célula desta tabela mostra uma comparação dos indicadores da categoria no período referência versus o período atual, assim a categoria é comparada com ela mesma, para todas as lojas."
      >
        <template v-slot:headers>
          <export-data
            :disabled="loadingTotalData"
            :data-fetcher="getTotalDataToExport"
            :filename="`Comparativo de Categorias - Total - ${resultsTabs[totalResultTab].name} - ${resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].name}`"
            ghost
          />
        </template>
        <mf-loading :boolean-loader="loadingTotalData" loader-type="table">
          <mf-tabs-menu v-model="totalResultTab" highlight-color="white" class="mt-4" :tabs="resultsTabs" :disabled="loadingTotalData">
            <template v-slot:children>
              <mf-tabs-menu v-model="totalResultSubtab" class="mt-4" :tabs="resultsTabs[totalResultTab].childrenTabs" :disabled="loadingTotalData" />
            </template>
          </mf-tabs-menu>
          <results-table
            ref="totalResultsTable"
            :categories="categories"
            :units="filteredUnits"
            :comparison-data="totalData"
            :metric-key="resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].metricKey"
            :custom-reference-getter="resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].customReferenceGetter"
            :custom-current-getter="resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].customCurrentGetter"
            :custom-percentage-calculator="resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].customPercentageCalculator"
            :custom-color-value-calculator="resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].customColorValueCalculator"
            :loading="loadingTotalData"
            :formatter="resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].formatter"
            :metric-description="`${resultsTabs[totalResultTab].name} - ${resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].name}`"
            :cell-information-configuration="{
              metric: `${resultsTabs[totalResultTab].name} - ${resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].name}`,
              metricKey: resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].metricKey,
              ...(resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].cellInformationConfiguration || {}),
            }"
            :reference-date="filters.referencyDate"
            :current-date="filters.currentDate"
            :quick-action="resultsTabs[totalResultTab].childrenTabs[totalResultSubtab].quickAction"
            :is-empty="isTotalEmpty"
          />
        </mf-loading>
      </mf-card-container>
    </mf-loading>
  </base-page-layout>
</template>
<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import { DateTime } from 'luxon'
import BasePageLayout from '@/components/BasePageLayout.vue'
import ResultsTabsConfiguration from '@/modules/produtos/comparativo-categorias/mocks/ResultsTabsConfiguration.js'
import { setData, getData } from '@/services/storage.js'

const categoryLocalStorageKey = 'categoryComparisonDefaultCategories'

export default {
  components: {
    BasePageLayout,
    ExportData: () => import('@/components/export-data/ExportData.vue'),
    DecompositionFilters: () => import('@/modules/empresa/decomposicao-faturamento/views/components/DecompositionFilters.vue'),
    ResultsTable: () => import('@/modules/produtos/comparativo-categorias/views/components/ResultsTable.vue'),
  },

  data: () => ({
    resultTab: 0,
    resultSubtab: 0,
    totalResultTab: 0,
    totalResultSubtab: 0,
    filters: {
      referencyDate: [],
      currentDate: [],
    },
    loadingComparisonData: true,
    loadingTotalData: true,
    comparisonData: [],
    totalData: [],
    listenToFilterEvents: false,
    isComparisonEmpty: false,
    isTotalEmpty: false,
    didMount: false,
  }),

  computed: {
    ...mapState({
      lastUpdate: (state) => state.lastUpdate,
      stateFilters: (state) => state.filters,
      stateCategoriesTree: (state) => state.produtos.categoriesTree,
    }),
    ...mapGetters(['units']),
    filteredUnits() {
      if (!this.stateFilters.unitsConfiguration?.length) {
        return this.units
      }

      const selectedUnitIds = this.stateFilters.unitsConfiguration.map((unit) => unit.value)
      return this.units.filter((unit) => selectedUnitIds.includes(unit.id_erp))
    },

    formatedLastUpdated() {
      if (!this.lastUpdate.last_updated) return ''
      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM-dd')
    },
    firstDayOfLastMonth() {
      if (!this.lastUpdate.last_updated) return ''
      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').plus({ months: -1 }).startOf('month').toFormat('yyyy-MM-dd')
    },
    firstDayReferencyMonth() {
      if (!this.lastUpdate.last_updated) return ''
      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').plus({ months: -1 }).toFormat('yyyy-MM-dd')
    },
    firstDayOfCurrentMonth() {
      if (!this.lastUpdate.last_updated) return ''
      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').startOf('month').toFormat('yyyy-MM-dd')
    },
    lastDay() {
      if (this.lastUpdate.last_updated) return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM-dd')
      return ''
    },

    resultsTabs() {
      return [...ResultsTabsConfiguration]
    },

    categories() {
      return this.stateFilters.categories
    },
  },

  watch: {
    'lastUpdate.last_updated': {
      handler() {
        this.filters.referencyDate = [this.firstDayOfLastMonth, this.firstDayReferencyMonth]
        this.filters.currentDate = [this.firstDayOfCurrentMonth, this.lastDay]
      },
      immediate: true,
    },
    filters: {
      deep: true,
      handler() {
        if (this.didMount) this.getData()
      },
    },
  },

  async mounted() {
    await this.categoriesTree({ should_trim_tree: true })

    // Get default categories filter
    const defaultCategories = this.getDefaultCategories()

    await this.getData({ categories: defaultCategories })

    this.didMount = true

    // Get on changed filter bus
    this.$nextTick(() => {
      // Set filters state externally
      const object = this.stateFilters
      object.categories = defaultCategories
      this.setFilters(object)
      this.$bus.$on('filter', this.getData)
    })
  },

  destroyed() {
    this.$bus.$off('filter')
  },
  methods: {
    ...mapWaitingActions({
      categoriesTree: 'categories-tree',
      categoryComparisons: 'category-comparisons',
    }),
    ...mapActions(['setFilters']),
    getComparisonDataToExport() {
      return this.$refs.comparisonResultsTable.exportData()
    },
    getTotalDataToExport() {
      return this.$refs.totalResultsTable.exportData()
    },
    async getData(filtersOverwrites = {}) {
      await Promise.all([this.getComparisonData(filtersOverwrites), this.getTotalData(filtersOverwrites)])
    },

    async getComparisonData(filtersOverwrites = {}) {
      this.loadingComparisonData = true
      const categories = filtersOverwrites.categories || this.stateFilters.categories
      const filters = {
        reference_period: this.filters.referencyDate,
        current_period: this.filters.currentDate,
        categories: categories.map((category) => category.id),
        units: this.stateFilters.unitsConfiguration || [],
        segments: this.stateFilters.segments || [],
        group_by_units: true,
      }
      try {
        const response = await this.categoryComparisons(filters)
        if (!response.data?.empty) {
          this.comparisonData = response.data
          this.setDefaultCategoriesOnLocalStorage(categories)
          this.isComparisonEmpty = false
        } else this.isComparisonEmpty = true
      } catch (e) {
        this.$snackbar({ message: 'Erro ao gerar métricas', snackbarColor: 'error' })
      }
      this.loadingComparisonData = false
    },
    async getTotalData(filtersOverwrites = {}) {
      this.loadingTotalData = true
      const categories = filtersOverwrites.categories || this.stateFilters.categories
      const filters = {
        reference_period: this.filters.referencyDate,
        current_period: this.filters.currentDate,
        categories: categories.map((category) => category.id),
        units: this.stateFilters.unitsConfiguration || [],
        segments: this.stateFilters.segments || [],
        group_by_units: false,
      }
      try {
        const response = await this.categoryComparisons(filters)
        if (!response.data?.empty) {
          this.totalData = response.data
          this.isTotalEmpty = false
        } else this.isTotalEmpty = true
      } catch (e) {
        this.$snackbar({ message: 'Erro ao gerar métricas', snackbarColor: 'error' })
      }
      this.loadingTotalData = false
    },
    getDefaultCategories() {
      const localStorageDefaultCategories = this.getDefaultCategoriesFromLocalStorage()
      if (localStorageDefaultCategories) {
        return localStorageDefaultCategories
      }
      const defaultCategories = this.stateCategoriesTree.children.map((child) => ({
        name: child.name,
        id: child.id,
      }))
      return defaultCategories
    },
    getDefaultCategoriesFromLocalStorage() {
      const rawCategories = getData(categoryLocalStorageKey, null)
      const accountId = this.$loggedUser?.id_account
      if (!rawCategories) return

      const categories = rawCategories[accountId]
      // TODO: this should refresh the category name!
      return categories
    },
    setDefaultCategoriesOnLocalStorage(categories) {
      let processedCategories = {}
      const rawCategories = getData(categoryLocalStorageKey, null)
      const accountId = this.$loggedUser?.id_account

      if (rawCategories) {
        processedCategories = rawCategories
      }
      processedCategories[accountId] = categories

      setData(categoryLocalStorageKey, processedCategories)
    },
  },
}
</script>

<style lang="scss">
.comparativo-produtos {
  .filters {
    .flex {
      padding: 10px;
    }
  }
  .metrics-table {
    tr,
    th {
      text-align: center;
    }

    .fixed-width {
      width: 130px;
    }
  }
  .metric-title {
    font-weight: bold;
    margin: 20px 0 10px 2px;
  }
  .period-title {
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 0;
  }
  .products-table {
    .positive-percent-color {
      background-color: #def1dd;
    }

    .negative-percent-color {
      background-color: #f0cccc;
    }
  }

  .action {
    text-align: center;
  }

  .comparasion-error {
    margin-top: 2px;
    padding-left: 12px;
    font-size: 12px;
    color: $vermelho-0;
  }
}
</style>
